import { Key, useCallback, useMemo, useState, useContext } from 'react';
import { Button, useDisclosure } from '@ds';
import { AdminPageHeader, DataTable, SearchInputWithHistory } from '@ui';
import { toast } from 'react-toastify';
import { AuthContext } from '@lp/core/auth';
import {
  ARCHIVE_CASES_LIST_FILTERS_BASE,
  ArchiveCasesFiltersState,
  DEFAULT_SORT_BY,
  getArchiveCasesTableColumnsLP,
  getRenderArchiveCasesTableCell,
  OUTCOME_SELECT_LIST,
  STATUS_SELECT_LIST,
} from '@features/components/Archive';
import { InvoiceModal } from '@features/modals/InvoiceModal';
import { useFilterQueryWithParams } from '@/hooks/useFilterQueryWithParams';
import { useArchiveCasesListWithFormattedQueryParams, useMoveCasesToClosedStatusMutation } from '@/queries/case';
import { retrieveUnifiedError } from '@/api/utils';
import { ArchivedCase, CaseStatus, Invoice, PaginatedCaseListFilters } from '@/api/apis/case';
import { ArchiveCasesPermissions, CaseDetailsPermissions, InvoicePermissions } from '@/permissions';
import { getPluralLabel } from '@/utils/helpers';
import { ArchiveCasesFilters } from './ArchiveCasesListTable/ArchiveCasesFilters';

const filterConfig = {
  statusList: STATUS_SELECT_LIST,
  outcomeList: OUTCOME_SELECT_LIST,
};

export const ArchiveCasesList = () => {
  const { checkPermission } = useContext(AuthContext);

  const hasViewInvoicePermission = checkPermission(InvoicePermissions.ReadInvoice);

  const hasArchiveAccessReadPermission =
    checkPermission(ArchiveCasesPermissions.CaseArchiveAccess90DaysRead) ||
    checkPermission(ArchiveCasesPermissions.CaseArchiveAccessIndefiniteRead);

  const hasChangeStatusPermission = checkPermission(CaseDetailsPermissions.CaseStatusUpdate);

  const caseDetailsRedirectUrl = '/case/:caseId';
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [invoiceState, setInvoiceState] = useState<{ invoiceList: Invoice[]; invoiceCaseId: string }>({
    invoiceList: [],
    invoiceCaseId: '',
  });

  const [selectedCases, setSelectedCases] = useState<ArchivedCase[]>([]);

  const isMarkButtonDisabled = useMemo(() => {
    return selectedCases.some((item) => item.status === CaseStatus.Finished);
  }, [selectedCases]);

  const archiveCaseTableColumns = useMemo(
    () => getArchiveCasesTableColumnsLP(hasArchiveAccessReadPermission),
    [hasArchiveAccessReadPermission],
  );

  const {
    data,
    isLoading,
    isFetching,
    filters,
    hasFilters,
    updateFilters,
    clearFilters,
    page,
    itemsPerPage,
    sortBy,
    searchText,
    setPage,
    setItemsPerPage,
    setSortBy,
    setSearchText,
  } = useFilterQueryWithParams<ArchiveCasesFiltersState, PaginatedCaseListFilters, ArchivedCase, string>({
    filterList: ARCHIVE_CASES_LIST_FILTERS_BASE,
    query: useArchiveCasesListWithFormattedQueryParams,
    defaultSortBy: DEFAULT_SORT_BY,
  });

  const { mutateAsync } = useMoveCasesToClosedStatusMutation();

  const onInvoiceModalOpen = useCallback(
    (invoices: Invoice[], caseId: string) => {
      setInvoiceState({
        invoiceList: invoices,
        invoiceCaseId: caseId,
      });
      onOpen();
    },
    [onOpen],
  );

  const handleSelectionChange = (key: Set<Key> | 'all', selectedData?: { items: ArchivedCase[] }) => {
    if (!selectedData?.items) {
      setSelectedCases([]);
      return;
    }

    if (key === 'all') {
      setSelectedCases(selectedData.items);
      return;
    }

    const ids = Array.from(key).map(String);

    if (!ids.length) {
      setSelectedCases([]);
      return;
    }

    const filteredData = selectedData.items.filter((item) => ids.includes(item.caseId));
    setSelectedCases(filteredData);
  };

  const handleSendCaseToCloseStatus = async () => {
    try {
      const response = await mutateAsync({
        caseIds: selectedCases.map((selectedCase) => selectedCase.caseId),
      });

      const { closedCases, notClosedCases } = response;

      if (closedCases.length > 0) {
        toast.success(
          `Following ${getPluralLabel(
            closedCases.length,
            'case',
            'was',
          )} moved to "Closed" status: ${closedCases.join(', ')}`,
        );
      }

      if (notClosedCases.length > 0) {
        toast.error(
          `Following ${getPluralLabel(
            notClosedCases.length,
            'case',
            'was',
          )} not moved to "Closed" status: ${notClosedCases.join(', ')}`,
        );
      }

      setSelectedCases([]);
    } catch (e) {
      const error = retrieveUnifiedError(e);
      toast.error(error.message);
    }
  };

  const renderTableCell = useCallback(
    (item: ArchivedCase, columnKey: Key) => {
      const renderFn = getRenderArchiveCasesTableCell(
        hasViewInvoicePermission,
        caseDetailsRedirectUrl,
        onInvoiceModalOpen,
      );

      return renderFn(item, columnKey);
    },
    [hasViewInvoicePermission, onInvoiceModalOpen],
  );

  return (
    <div className="flex flex-col place-items-stretch gap-9 p-6">
      <AdminPageHeader title="Archive">
        {hasChangeStatusPermission && (
          <Button size="sm" isDisabled={!isMarkButtonDisabled} onClick={handleSendCaseToCloseStatus}>
            Mark as Case Closed
          </Button>
        )}
      </AdminPageHeader>

      <div className="flex flex-col gap-4">
        <div className="flex flex-row flex-wrap gap-4">
          <SearchInputWithHistory
            handleSearch={setSearchText}
            searchText={searchText}
            storageKey="archiveCasesHistory"
          />

          <ArchiveCasesFilters
            filterConfig={filterConfig}
            filters={filters}
            hasFilters={hasFilters}
            updateFilters={updateFilters}
            clearFilters={clearFilters}
          />
        </div>

        <DataTable
          idKey="caseId"
          label="Archive Cases"
          columns={archiveCaseTableColumns}
          renderCell={renderTableCell}
          data={data?.items || []}
          isLoading={isLoading}
          isFetching={isFetching}
          totalItems={data?.totalItems || 0}
          totalPages={data?.totalPages || 1}
          page={page}
          itemsPerPage={itemsPerPage}
          sortBy={sortBy}
          onPageChange={setPage}
          onItemsPerPageChange={setItemsPerPage}
          onSortByChange={setSortBy}
          onSelectionChange={(keys) => {
            const raw = keys as unknown;

            if (raw instanceof Set) {
              handleSelectionChange(raw as Set<Key>, data);
              return;
            }

            handleSelectionChange('all', data);
          }}
          selectionMode={hasChangeStatusPermission ? 'multiple' : 'none'}
          disallowEmptySelection={false}
        />
      </div>

      <InvoiceModal
        isOpen={isOpen}
        onClose={onClose}
        invoices={invoiceState.invoiceList}
        caseId={invoiceState.invoiceCaseId}
      />
    </div>
  );
};
